/**
 * Expertises — Pages publiques SEO de CONFILEX (verticales + professions).
 *
 * Routes :
 *   /expertises                          → Index (5 verticales)
 *   /expertises/:verticalId              → Page verticale (ex: juridique)
 *   /expertises/:verticalId/:professionId → Page profession (ex: juridique/avocat)
 *
 * Le composant détecte la route via params et appelle l'endpoint approprié.
 * Toutes les pages injectent SEO meta + JSON-LD via react-helmet-async.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight, BookOpen, MapPin, Star, ChevronRight, Sparkles,
  Scale, Calculator, Landmark, Building, TrendingUp,
} from "lucide-react";
import { apiClient } from "@/lib/api";

const VERTICAL_ICONS = {
  juridique: Scale, "chiffre-fiscalite": Calculator,
  patrimoine: Landmark, immobilier: Building, financement: TrendingUp,
};

function SeoHead({ seo, canonical, jsonld }) {
  if (!seo) return null;
  const ld = Array.isArray(jsonld) ? jsonld : (jsonld ? [jsonld] : []);
  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description || ""} />
      {seo.keywords && <meta name="keywords" content={seo.keywords.join(", ")} />}
      {canonical && <link rel="canonical" href={canonical} />}
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description || ""} />
      <meta property="og:type" content={seo.og_type || "website"} />
      {canonical && <meta property="og:url" content={canonical} />}
      {ld.map((j, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(j)}
        </script>
      ))}
    </Helmet>
  );
}

function PageHeader({ kicker, title, subtitle }) {
  return (
    <header className="bg-gradient-to-br from-[#0B3A2A] to-[#0B3A2A] text-white">
      <div className="max-w-5xl mx-auto px-5 py-12 sm:py-16">
        <p className="text-[10px] uppercase tracking-[0.32em] text-[#C9A961] mb-3">{kicker}</p>
        <h1 className="font-serif italic text-3xl sm:text-5xl leading-tight">{title}</h1>
        {subtitle && (
          <p className="text-sm sm:text-base text-white/80 mt-4 max-w-2xl leading-relaxed">{subtitle}</p>
        )}
      </div>
    </header>
  );
}

function ArticleCard({ a }) {
  return (
    <Link
      to={`/insights/${a.slug}`}
      className="block bg-white border border-slate-100 hover:border-[#C9A961]/50 rounded-xl p-4 transition group"
      data-testid="expertise-article-card"
    >
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#8A6E3A] mb-1.5">Article</p>
      <h3 className="font-serif italic text-[#0B3A2A] text-lg leading-tight group-hover:text-[#8A6E3A] transition line-clamp-2">
        {a.title}
      </h3>
      {a.excerpt && (
        <p className="text-[12px] text-slate-600 mt-2 line-clamp-3 leading-relaxed">{a.excerpt}</p>
      )}
      <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-3">
        {a.author_name && <span>{a.author_name}</span>}
        {a.reading_time && <span>· {a.reading_time} min</span>}
      </div>
    </Link>
  );
}

function ExpertCard({ e }) {
  // top_domain est fourni inline par les endpoints /api/expertises/* (pas de N+1)
  const topDomain = e.top_domain;

  return (
    <Link
      to={`/spécialistes/${e.id}`}
      className="flex items-start gap-3 bg-white border border-slate-100 hover:border-[#C9A961]/50 rounded-xl p-3 transition"
      data-testid="expertise-spécialiste-card"
    >
      <div className="w-12 h-12 rounded-lg bg-[#C9A961]/15 flex items-center justify-center shrink-0 overflow-hidden">
        {e.photo
          ? <img src={e.photo} alt="" className="w-full h-full object-cover" />
          : <span className="font-serif text-[#8A6E3A]">{(e.first_name?.[0] || "")}{(e.last_name?.[0] || "")}</span>}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[#0B3A2A] font-medium truncate">
          {e.first_name} {e.last_name}
        </p>
        <p className="text-[11px] text-slate-500 truncate">
          {e.profession || "—"}{e.city ? ` · ${e.city}` : ""}
        </p>
        {topDomain && (
          <p className="text-[10px] text-[#8A6E3A] mt-1 truncate" data-testid="spécialiste-card-top-domain">
            {topDomain.label}
            {topDomain.articles > 0 && (
              <span className="text-slate-400 italic ml-1">· {topDomain.articles} publication{topDomain.articles > 1 ? "s" : ""}</span>
            )}
          </p>
        )}
      </div>
    </Link>
  );
}

function FaqBlock({ items }) {
  if (!items?.length) return null;
  return (
    <section data-testid="expertise-faq">
      <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">Questions fréquentes</h2>
      <div className="space-y-3">
        {items.map((q, i) => (
          <details key={i} className="bg-white border border-slate-100 rounded-xl px-4 py-3 group">
            <summary className="text-sm font-medium text-[#0B3A2A] cursor-pointer flex items-center justify-between">
              {q.q}
              <ChevronRight className="w-4 h-4 text-[#C9A961] group-open:rotate-90 transition" />
            </summary>
            <p className="text-[13px] text-slate-700 leading-relaxed mt-2">{q.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

// =========================================================================
// PAGE 1 : Index /expertises
// =========================================================================
export function ExpertisesIndex() {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiClient.get("/expertises").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return <div className="min-h-screen bg-[#FAF7F1]" data-testid="expertises-index" />;

  return (
    <div className="min-h-screen bg-[#FAF7F1]" data-testid="expertises-index">
      <SeoHead
        seo={data.seo}
        canonical={`${window.location.origin}/expertises`}
        jsonld={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "CONFILEX",
          "url": window.location.origin,
        }}
      />
      <PageHeader
        kicker="CONFILEX · INFRASTRUCTURE EXPERTE"
        title="Expertises CONFILEX"
        subtitle={`${data.totals.verticals} verticales métiers · ${data.totals.professions} professions · ${data.totals.published_articles} publications expertes`}
      />
      <main className="max-w-5xl mx-auto px-5 py-12 space-y-10">
        <section>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.verticals.map((v) => {
              const Icon = VERTICAL_ICONS[v.id] || Sparkles;
              return (
                <Link
                  key={v.id}
                  to={`/expertises/${v.id}`}
                  data-testid={`vertical-card-${v.id}`}
                  className="group bg-white border border-slate-100 hover:border-[#C9A961]/60 rounded-2xl p-5 transition shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#C9A961]/15 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#8A6E3A]" />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400">
                      {v.professions_count} prof.
                    </p>
                  </div>
                  <h2 className="font-serif italic text-xl text-[#0B3A2A] group-hover:text-[#8A6E3A] transition leading-tight">
                    {v.label}
                  </h2>
                  <p className="text-[12px] text-slate-600 mt-1.5 leading-relaxed">{v.tagline}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {v.professions.slice(0, 4).map((p) => (
                      <span key={p.id} className="text-[10px] px-2 py-0.5 rounded-full bg-[#FAF7F1] text-slate-600 border border-slate-200">
                        {p.label}
                      </span>
                    ))}
                    {v.professions.length > 4 && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full text-slate-500">
                        +{v.professions.length - 4}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-[11px] text-slate-500 italic">
                      {v.articles_count} publication{v.articles_count > 1 ? "s" : ""}
                    </span>
                    <ArrowRight className="w-4 h-4 text-[#C9A961]" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

// =========================================================================
// PAGE 2 : Page verticale /expertises/:verticalId
// =========================================================================
export function VerticalLanding() {
  const { verticalId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    apiClient.get(`/expertises/v/${verticalId}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [verticalId]);

  if (notFound) return <NotFoundView />;
  if (!data) return <div className="min-h-screen bg-[#FAF7F1]" data-testid="vertical-landing" />;

  const Icon = VERTICAL_ICONS[data.vertical.id] || Sparkles;

  return (
    <div className="min-h-screen bg-[#FAF7F1]" data-testid="vertical-landing">
      <SeoHead seo={data.seo} canonical={data.canonical_url} jsonld={data.jsonld} />
      <PageHeader
        kicker={`CONFILEX · VERTICALE ${data.vertical.label.toUpperCase()}`}
        title={data.vertical.label}
        subtitle={data.vertical.tagline}
      />

      <main className="max-w-5xl mx-auto px-5 py-12 space-y-12">
        {/* Professions de la verticale */}
        <section>
          <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">
            <Icon className="w-5 h-5 text-[#C9A961] inline mr-2" />
            Professions ({data.professions.length})
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {data.professions.map((p) => (
              <Link
                key={p.id}
                to={`/expertises/${data.vertical.id}/${p.id}`}
                data-testid={`profession-card-${p.id}`}
                className="bg-white border border-slate-100 hover:border-[#C9A961]/50 rounded-xl p-3 transition"
              >
                <p className="text-sm text-[#0B3A2A] font-medium leading-tight">
                  {p.short_label || p.label}
                </p>
                {p.common_specialties?.length > 0 && (
                  <p className="text-[11px] text-slate-500 mt-1 line-clamp-1 italic">
                    {p.common_specialties.slice(0, 3).join(" · ")}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </section>

        {/* Articles */}
        {data.articles.length > 0 && (
          <section>
            <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">
              <BookOpen className="w-5 h-5 text-[#C9A961] inline mr-2" />
              Publications expertes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.articles.map((a) => <ArticleCard key={a.id} a={a} />)}
            </div>
          </section>
        )}

        {/* Spécialistes */}
        {data.experts.length > 0 && (
          <section>
            <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">
              Experts présents ({data.experts_total})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.experts.map((e) => <ExpertCard key={e.id} e={e} />)}
            </div>
          </section>
        )}

        {/* FAQ */}
        <FaqBlock items={data.faq} />

        {/* Maillage interne — autres verticales */}
        <section className="border-t border-slate-200 pt-8">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-3">
            Autres verticales CONFILEX
          </p>
          <div className="flex flex-wrap gap-2">
            {data.related_verticals.map((v) => (
              <Link
                key={v.id}
                to={`/expertises/${v.id}`}
                className="text-[12px] px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[#0B3A2A] hover:border-[#C9A961] hover:text-[#8A6E3A] transition"
              >
                {v.label}
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

// =========================================================================
// PAGE 3 : Page profession /expertises/:verticalId/:professionId
// =========================================================================
export function ProfessionLanding() {
  const { professionId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    apiClient.get(`/expertises/p/${professionId}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [professionId]);

  if (notFound) return <NotFoundView />;
  if (!data) return <div className="min-h-screen bg-[#FAF7F1]" data-testid="profession-landing" />;

  return (
    <div className="min-h-screen bg-[#FAF7F1]" data-testid="profession-landing">
      <SeoHead seo={data.seo} canonical={data.canonical_url} jsonld={data.jsonld} />
      <PageHeader
        kicker={`CONFILEX · ${data.vertical.label.toUpperCase()}`}
        title={data.profession.label}
        subtitle={(data.profession.common_specialties || []).slice(0, 4).join(" · ")}
      />

      <main className="max-w-5xl mx-auto px-5 py-12 space-y-12">
        {/* Breadcrumb */}
        <nav className="text-[12px] text-slate-500 -mt-6">
          <Link to="/expertises" className="hover:text-[#8A6E3A]">Expertises</Link>
          <span className="mx-2">/</span>
          <Link to={`/expertises/${data.vertical.id}`} className="hover:text-[#8A6E3A]">{data.vertical.label}</Link>
          <span className="mx-2">/</span>
          <span className="text-[#0B3A2A]">{data.profession.label}</span>
        </nav>

        {/* Articles */}
        {data.articles.length > 0 && (
          <section>
            <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">
              <BookOpen className="w-5 h-5 text-[#C9A961] inline mr-2" />
              Publications · {data.profession.label}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.articles.map((a) => <ArticleCard key={a.id} a={a} />)}
            </div>
          </section>
        )}

        {/* Spécialistes */}
        {data.experts.length > 0 ? (
          <section>
            <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">
              Experts disponibles ({data.experts_total})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.experts.map((e) => <ExpertCard key={e.id} e={e} />)}
            </div>
          </section>
        ) : (
          <section className="bg-white border border-slate-100 rounded-2xl p-6 text-center">
            <MapPin className="w-6 h-6 text-[#C9A961] mx-auto mb-2" />
            <p className="text-sm text-[#0B3A2A] font-medium">
              Aucun {data.profession.label.toLowerCase()} référencé pour le moment.
            </p>
            <p className="text-[12px] text-slate-500 mt-2 italic">
              CONFILEX peut effectuer une recherche personnalisée et constituer une fiche
              spécialiste complète à partir de sources publiques vérifiées. Frais de recherche
              et de mise en relation : 89 € TTC. Ce montant ne s'applique qu'aux clients
              sollicitant un spécialiste externe au réseau référencé CONFILEX.
            </p>
          </section>
        )}

        {/* FAQ */}
        <FaqBlock items={data.faq} />

        {/* Maillage interne — autres professions */}
        <section className="border-t border-slate-200 pt-8">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-3">
            Autres professions · {data.vertical.label}
          </p>
          <div className="flex flex-wrap gap-2">
            {data.related_professions.map((p) => (
              <Link
                key={p.id}
                to={`/expertises/${data.vertical.id}/${p.id}`}
                className="text-[12px] px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[#0B3A2A] hover:border-[#C9A961] hover:text-[#8A6E3A] transition"
              >
                {p.label}
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function NotFoundView() {
  return (
    <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 text-center max-w-md shadow-sm">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">CONFILEX</p>
        <h1 className="font-serif italic text-2xl text-[#0B3A2A] mt-2">Cette page n'existe pas</h1>
        <Link to="/expertises" className="text-sm text-[#8A6E3A] mt-4 inline-block hover:underline">
          ← Retour aux expertises
        </Link>
      </div>
    </div>
  );
}

// Export par défaut : la page index (utilisé directement par App.js)
export default ExpertisesIndex;
